import { useEffect, useState } from 'react';
import type { ISecodaEntity } from '../../../../api';
import { queryClient, secodaEntitiesQueryKeyFactory } from '../../../../api';
import { fetchSecodaEntity } from '../../../../api/hooks/secodaEntity/fetchSecodaEntities';
import { SecodaEntityIcon } from '../../../SecodaEntity';

export const useMultiSelectorTargetSelected = (
	entity: { related_entities?: ISecodaEntity[] },
	selected: string[]
) => {
	const [selectedEntities, setSelectedEntities] = useState<
		Array<{ label: string; value: string; icon: JSX.Element }>
	>([]);

	useEffect(() => {
		if (
			selected.every((id) =>
				entity.related_entities?.some((re) => re.id === id)
			)
		) {
			setSelectedEntities(
				selected.map((id) => ({
					label:
						entity.related_entities?.find((re) => re.id === id)?.title ||
						'Untitled',
					value: id,
					icon: (
						<SecodaEntityIcon
							entity={entity.related_entities?.find((re) => re.id === id)}
							size={20}
						/>
					),
				}))
			);
			return;
		}

		const abortController = new AbortController();
		const { signal } = abortController;

		const fetchEntities = async () => {
			try {
				const entities = await Promise.all(
					selected?.map(async (id) => {
						let relatedEntity = entity.related_entities?.find(
							(re) => re.id === id
						);
						if (!relatedEntity) {
							try {
								// First, try to get the entity from the query cache
								relatedEntity = queryClient.getQueryData(
									secodaEntitiesQueryKeyFactory.byId(id)
								);

								// If not in cache, fetch it
								if (!relatedEntity) {
									relatedEntity = await fetchSecodaEntity(id);
								}
							} catch (error: unknown) {
								if (error instanceof Error && error.name === 'AbortError') {
									return null;
								}
								return null;
							}
						}
						return {
							label: relatedEntity.title || 'Untitled',
							value: relatedEntity.id,
							icon: <SecodaEntityIcon entity={relatedEntity} size={20} />,
						};
					}) || []
				);
				if (!signal.aborted) {
					setSelectedEntities(entities.filter((e) => e !== null));
				}
			} catch (error: unknown) {
				if (error instanceof Error && error.name !== 'AbortError') {
					// Error fetching entities - silently fail
				}
			}
		};

		fetchEntities();

		// eslint-disable-next-line consistent-return
		return () => {
			abortController.abort();
		};
	}, [entity.related_entities, selected]);

	return selectedEntities;
};
